import { Slot } from "@radix-ui/react-slot";
import clsx from "clsx";
import {
  forwardRef,
  HTMLAttributes,
  ReactNode,
  ImgHTMLAttributes,
  AnchorHTMLAttributes,
} from "react";
import { AspectRatio } from "@repo/ui/aspect-ratio";
import * as Flex from "@repo/ui/flex";
import { ProductPriceProps, ProductPrice } from "@repo/ui/product-price";
import { Text } from "@repo/ui/text";
import { ImageSizes } from "@repo/utils/image-presets";
import { CloudinaryImage } from "../cloudinary-image";
import classes from "./product-tile.module.css";

type ProductTileRootProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
  asChild?: boolean;
};

export const Root = forwardRef<HTMLAnchorElement, ProductTileRootProps>(
  ({ asChild, className, ...props }, ref) => {
    const Comp = asChild ? Slot : "a";

    return (
      <Comp
        ref={ref as React.Ref<HTMLAnchorElement>}
        className={clsx(classes.root, className)}
        data-testid="product-tile"
        {...props}
      />
    );
  },
);

Root.displayName = "ProductTileRoot";

type ProductTileProductImageProps = ImgHTMLAttributes<HTMLImageElement> & {
  imageUrl: string;
  altText: string;
  sourceSetProfile: ImageSizes;
  srcSize: number;
};

export const ProductImage = forwardRef<
  HTMLImageElement,
  ProductTileProductImageProps
>(({ imageUrl, altText, ...props }, ref) => {
  return (
    <AspectRatio ratio={952 / 1215} className={classes.aspectRatio}>
      <CloudinaryImage alt={altText} url={imageUrl} ref={ref} {...props} />
    </AspectRatio>
  );
});

ProductImage.displayName = "ProductTileImage";

type ProductTileProductVideoProps = HTMLAttributes<HTMLVideoElement> & {
  videoUrl: string;
  autoplay?: boolean;
  altText: string;
  srcLang?: string;
  label?: string;
};

export const ProductVideo = forwardRef<
  HTMLVideoElement,
  ProductTileProductVideoProps
>(
  (
    {
      videoUrl,
      autoplay = false,
      altText,
      srcLang,
      label,
      className,
      ...props
    },
    ref,
  ) => {
    return (
      <AspectRatio ratio={952 / 1215}>
        <video
          ref={ref}
          className={clsx(classes.video, className)}
          controls
          muted={autoplay}
          autoPlay={autoplay}
          src={videoUrl}
          aria-label={altText}
          {...props}
        >
          <track
            kind="captions"
            srcLang={srcLang}
            src={`${videoUrl}.vtt`}
            label={label}
            default
          />
        </video>
      </AspectRatio>
    );
  },
);

ProductVideo.displayName = "ProductTileVideo";

type ProductTileTitleProps = HTMLAttributes<HTMLParagraphElement> & {
  children: React.ReactNode;
  maxLines?: number;
};

export const Title = forwardRef<HTMLParagraphElement, ProductTileTitleProps>(
  ({ children, maxLines = 1, className, ...props }, ref) => {
    return (
      <Text
        ref={ref}
        maxLines={maxLines}
        className={clsx(className)}
        {...props}
      >
        {children}
      </Text>
    );
  },
);

Title.displayName = "ProductTileTitle";

export const Price = forwardRef<HTMLParagraphElement, ProductPriceProps>(
  ({ className, ...props }, ref) => {
    return <ProductPrice ref={ref} className={clsx(className)} {...props} />;
  },
);

Price.displayName = "ProductTilePrice";

type ProductTileBadgeProps = HTMLAttributes<HTMLDivElement> & {
  text: string;
  icon?: ReactNode;
};

export const Badge = forwardRef<HTMLDivElement, ProductTileBadgeProps>(
  ({ text, icon, ...props }, ref) => {
    return (
      <Flex.Container
        ref={ref}
        {...props}
        justify="center"
        align="center"
        className={clsx(classes.badge, props.className)}
      >
        {icon && <span>{icon}</span>}
        <span>{text}</span>
      </Flex.Container>
    );
  },
);

Badge.displayName = "ProductTileBadge";
